import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/StyledInput.css';
import './InventorySettingsPage.css';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';

function InventorySettingsPage({ onNavigate }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [defaultBaseUomId, setDefaultBaseUomId] = useState('');
  const [savedBaseUomId, setSavedBaseUomId] = useState('');
  const [defaultProductTypeId, setDefaultProductTypeId] = useState('');
  const [savedProductTypeId, setSavedProductTypeId] = useState('');
  const [defaultUomControlTypeId, setDefaultUomControlTypeId] = useState('');
  const [savedUomControlTypeId, setSavedUomControlTypeId] = useState('');
  const [uoms, setUoms] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [uomControlTypes, setUomControlTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get('/api/inventory-settings'),
      axios.get('/api/uoms', { params: { status: 'active' } }),
      axios.get('/api/product-types', { params: { status: 'active' } }),
      axios.get('/api/uom-control-types', { params: { status: 'active' } }),
    ])
      .then(([settingsRes, uomRes, productTypeRes, uomControlTypeRes]) => {
        const loadedUom = settingsRes.data.data?.default_base_uom_id ?? '';
        setDefaultBaseUomId(loadedUom);
        setSavedBaseUomId(loadedUom);
        const loadedProductType = settingsRes.data.data?.default_product_type_id ?? '';
        setDefaultProductTypeId(loadedProductType);
        setSavedProductTypeId(loadedProductType);
        const loadedUomControlType = settingsRes.data.data?.default_uom_control_type_id ?? '';
        setDefaultUomControlTypeId(loadedUomControlType);
        setSavedUomControlTypeId(loadedUomControlType);
        setUoms(uomRes.data.data || []);
        setProductTypes(productTypeRes.data.data || []);
        setUomControlTypes((uomControlTypeRes.data.data || []).filter((t) => t.can_track));
      })
      .catch((err) => showError(err, 'Failed to load Inventory Settings'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess('');
    try {
      await axios.put('/api/inventory-settings', {
        default_base_uom_id: defaultBaseUomId === '' ? null : Number(defaultBaseUomId),
        default_product_type_id: defaultProductTypeId === '' ? null : Number(defaultProductTypeId),
        default_uom_control_type_id: defaultUomControlTypeId === '' ? null : Number(defaultUomControlTypeId),
      });
      setSavedBaseUomId(defaultBaseUomId);
      setSavedProductTypeId(defaultProductTypeId);
      setSavedUomControlTypeId(defaultUomControlTypeId);
      setSuccess('Inventory settings saved.');
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setDefaultBaseUomId(savedBaseUomId);
    setDefaultProductTypeId(savedProductTypeId);
    setDefaultUomControlTypeId(savedUomControlTypeId);
    setSuccess('');
    if (onNavigate) onNavigate('inventory-dashboard');
  };

  if (loading) {
    return <div style={{ width: '100%', maxWidth: 600, margin: '32px auto' }}>Loading...</div>;
  }

  return (
    <div style={{ width: '100%', maxWidth: 600, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 }}>
      <h2 style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 20 }}>Inventory Settings</h2>
      <p style={{ color: '#666', fontSize: 14, marginTop: 0 }}>Tenant-wide defaults for the inventory module.</p>

      <form onSubmit={handleSave} className="inventory-settings-form" style={{ marginTop: 16 }}>
        <label>Default Product Type</label><br />
        <select
          className="styled-input"
          value={defaultProductTypeId}
          onChange={(e) => setDefaultProductTypeId(e.target.value)}
          style={{ width: 220 }}
        >
          <option value="">(none)</option>
          {productTypes.map((s) => (
            <option key={s.id} value={s.id}>{s.code} — {s.name}</option>
          ))}
        </select>
        {onNavigate && (
          <button
            type="button"
            onClick={() => onNavigate('inventory-product-types')}
            style={{ marginLeft: 10, background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0 }}
          >
            Manage product types
          </button>
        )}
        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
          New Products default to this Product Type. Changes do not affect existing Products.
        </div>

        <label style={{ display: 'block', marginTop: 16 }}>Default Base Unit of Measure</label>
        <select
          className="styled-input"
          value={defaultBaseUomId}
          onChange={(e) => setDefaultBaseUomId(e.target.value)}
          style={{ width: 220 }}
        >
          <option value="">(none)</option>
          {uoms.map((u) => (
            <option key={u.id} value={u.id}>{u.code} — {u.name}</option>
          ))}
        </select>
        {onNavigate && (
          <button
            type="button"
            onClick={() => onNavigate('inventory-uom')}
            style={{ marginLeft: 10, background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0 }}
          >
            Manage units of measure
          </button>
        )}
        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
          New Products default to this Base UoM. Changes do not affect existing Products.
        </div>

        <label style={{ display: 'block', marginTop: 16 }}>Default UoM Control Type</label>
        <select
          className="styled-input"
          value={defaultUomControlTypeId}
          onChange={(e) => setDefaultUomControlTypeId(e.target.value)}
          style={{ width: 220 }}
        >
          <option value="">(none)</option>
          {uomControlTypes.map((t) => (
            <option key={t.id} value={t.id}>{t.code} — {t.name}</option>
          ))}
        </select>
        {onNavigate && (
          <button
            type="button"
            onClick={() => onNavigate('inventory-uom-control-types')}
            style={{ marginLeft: 10, background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0 }}
          >
            Manage UoM Control Types
          </button>
        )}
        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
          A new Product's Base UoM row defaults to this Control Type (must have Can Track on). Changes do not affect existing Products.
        </div>

        {success && <div style={{ color: '#2e7d32', marginTop: 10 }}>{success}</div>}

        <div style={{ marginTop: 16 }}>
          <SaveButton loading={saving} />
          <CancelButton onClick={handleCancel} disabled={saving} style={{ marginLeft: 8 }} />
        </div>
      </form>
    </div>
  );
}

export default InventorySettingsPage;
